import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class GeoConversionService {
  private resolution = 4.776300731784036; // Resolution for zoom 15

  private mercatorToLatLon(x: number, y: number): { lat: number; lng: number } {
    const lon = (x * 180) / 20037508.34;
    const lat = (Math.atan(Math.exp((y * Math.PI) / 20037508.34)) * 360) / Math.PI - 90;
    return { lat: this.round(lat, 7), lng: this.round(lon, 7) };
  }

  private round(value: number, precision: number): number {
    const multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  }

  private crosses180Meridian(coordinates: number[][]): boolean {
    return coordinates.some(([x]) => x > 20037508.34 || x < -20037508.34);
  }

  private adjustCoordinatesFor180(coordinates: number[][]): number[][] {
    return coordinates.map(([x, y]) => [
      x > 20037508.34 ? x - 2 * 20037508.34 : x < -20037508.34 ? x + 2 * 20037508.34 : x,
      y,
    ]);
  }

  private processGeometry(geometry: any): any {
    if (!geometry.coordinates) {
      throw new Error('Invalid geometry: coordinates must be a non-empty array.');
    }

    if (geometry.type === 'Point') {
      return this.processPoint(geometry);
    } else if (geometry.type === 'Polygon') {
      return this.processPolygon(geometry);
    } else if (geometry.type === 'MultiPolygon') {
      return this.processMultiPolygon(geometry);
    }

    return geometry;
  }

  private processPoint(geometry: any): any {
    const [x, y] = geometry.coordinates;
    const { lat, lng } = this.mercatorToLatLon(x, y);

    return {
      ...geometry,
      coordinates: JSON.stringify([{ lat, lng }]),
      boundingBox: {
        minX: x.toString(),
        maxX: x.toString(),
        minY: y.toString(),
        maxY: y.toString(),
      },
      center: {
        x: x.toString(),
        y: y.toString(),
      },
    };
  }

  private processPolygon(geometry: any): any {
    let processedCoordinates = geometry.coordinates;

    if (this.crosses180Meridian(geometry.coordinates[0])) {
      processedCoordinates = [this.adjustCoordinatesFor180(geometry.coordinates[0])];
    }

    return this.processCoordinates([processedCoordinates], geometry); // Wrap it for uniform handling
  }

  private processMultiPolygon(geometry: any): any {
    const processedCoordinates = geometry.coordinates.map((polygon: number[][]) =>
      // @ts-ignore
      this.crosses180Meridian(polygon[0]) ? [this.adjustCoordinatesFor180(polygon[0])] : polygon
    );

    return this.processCoordinates(processedCoordinates, geometry);
  }

  private processCoordinates(coordinates: number[][][][], geometry: any): any {
    const boundingBox = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };

    const latLngCoordinates: any[] = [];

    coordinates.forEach((polygon) => {
      const polygonRings = polygon.map((ring) => {
        return ring.map(([x, y]) => {
          const { lat, lng } = this.mercatorToLatLon(x, y);

          boundingBox.minX = Math.min(boundingBox.minX, x);
          boundingBox.maxX = Math.max(boundingBox.maxX, x);
          boundingBox.minY = Math.min(boundingBox.minY, y);
          boundingBox.maxY = Math.max(boundingBox.maxY, y);

          return { lat, lng };
        });
      });

      latLngCoordinates.push(...polygonRings);
    });

    const center = {
      x: (boundingBox.minX + boundingBox.maxX) / 2,
      y: (boundingBox.minY + boundingBox.maxY) / 2,
    };

    return {
      ...geometry,
      coordinates: JSON.stringify(latLngCoordinates),
      boundingBox: {
        minX: boundingBox.minX.toString(),
        maxX: boundingBox.maxX.toString(),
        minY: boundingBox.minY.toString(),
        maxY: boundingBox.maxY.toString(),
      },
      center: {
        x: center.x.toString(),
        y: center.y.toString(),
      },
    };
  }

  public processApiResponse(response: any): any {
    if (!response.data || !response.data.features) {
      throw new Error('Invalid API response format');
    }

    const features = response.data.features.map((feature: any) => {
      const geometry = this.processGeometry(feature.geometry);

      return {
        ...feature,
        geometry,
      };
    });

    return {
      ...response,
      data: {
        ...response.data,
        features,
      },
    };
  }
}
